import type {
  ChangeEvent,
  ClipboardEventHandler,
  KeyboardEventHandler,
  Ref
} from 'react';
import React, {
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { ClassNameProp, ValidIconName } from '..';
import { Icon } from '..';
import _ from 'lodash';
import { isEmptyString } from '../../util';
import type { CommonInputProps } from './CommonInputProps';
import { intl } from '../../lib';
import { ValidationErrorMessage } from './internal';

/**
 * Props for a {@link TextInput} component.
 * @author agd
 */
export interface TextInputProps
  extends ClassNameProp,
    CommonInputProps<string> {
  /**
   * The type of input element.
   * This element only supports 'text' and 'password'.
   */
  type: 'text' | 'password';
  /**
   * Optional placeholder of the input. The placeholder will only be shown
   * when the input is either focused or not empty.
   */
  placeholder?: string;
  /**
   * Optional icon to display.
   */
  leftIcon?: ValidIconName;
  /**
   * Optional right icon or other component like Tool to display.
   *
   * {@link allowShowPassword} does not work in conjunction with this option.
   */
  rightIcon?: ValidIconName | JSX.Element;
  /**
   * If showing the password is allowed (this only has an effect, if the type is set to 'password').
   *
   * {@link rightIcon} does not work in conjunction with this option.
   */
  allowShowPassword?: boolean;
  /**
   * If it's a multiline input or not.
   */
  multiline?: boolean;
  /**
   * Number of rows the textarea should take if multiline is true.
   */
  multilineRows?: number;
  /**
   * 'empty' value state controlled from the outside.
   *
   * This property is only ever really necessary when using an external library which manages the TextInput
   * component by directly accessing the DOM instead of using React.
   *
   * Generally, usage of this property should be avoided unless absolutely necessary.
   */
  valueEmpty?: boolean;
  /**
   * The auto complete hint for the input element according to HTML specification.
   */
  autoComplete?: 'password' | 'one-time-code';
  /**
   * The input mode for the input element. Currently only 'text' and 'numeric' input modes are supported.
   */
  inputMode?: 'text' | 'numeric' | 'decimal';
  /**
   * Flag to automatically focus on input element once it gets rendered.
   */
  autoFocus?: boolean;
  /**
   * Handler for when the user presses keyboard key.
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /**
   * Handler which will be called when something is pasted into the text input.
   */
  onPaste?: ClipboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /**
   * aria-label of the input. Caution! Normally does not have to be set for WCAG, please only use if necessary for special edge cases
   */
  'aria-label'?: string;
  /**
   * aria-describedby of the input. Caution! Normally does not have to be set for WCAG, please only use if necessary for special edge cases. This would also override the placeholder logic.
   */
  'aria-describedby'?: string;
}

/**
 * A simple text input field.
 *
 * The right icon can only be shown if type !== 'password' or allowShowPassword is false.
 *
 * The forwardRef is necessary because some libraries, e.g. the flatpickr date picker, expect a ref to the input element.
 * See {@link https://reactjs.org/docs/forwarding-refs.html} for details.
 *
 * @return the created {@link JSX.Element}.
 * @author agd
 * @param properties the {@link TextInputProps}
 * @constructor
 */
export const TextInput = forwardRef(function TextInput(
  {
    debounceWait = 1000,
    allowShowPassword = false,
    multiline = false,
    multilineRows = 4,
    className = '',
    condensed = false,
    readOnly = false,
    ...props
  }: TextInputProps,
  ref: Ref<HTMLInputElement> | Ref<HTMLTextAreaElement>
): JSX.Element {
  const controlled = props.value !== undefined;
  // these 2 states are only really necessary for IE due to :focus-within not working correctly
  const [valueEmpty, setValueEmpty] = useState(
    isEmptyString(props.value) && isEmptyString(props.defaultValue)
  );

  // update component state when updated from the outside
  useEffect(() => {
    if (controlled) {
      setValueEmpty(isEmptyString(props.value));
    }
  }, [props.value, controlled]);

  // decorate change callback event with debounce if necessary
  let onChange = props.onChange;
  if (!controlled && props.onChange && debounceWait > 0) {
    onChange = _.debounce(props.onChange, debounceWait);
  }

  // onBlur event for when the input element loses focus
  const onBlurProp = props.onBlur;
  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // update input element css
      setValueEmpty(isEmptyString(event.target.value));
      onBlurProp?.();
    },
    [onBlurProp]
  );
  // onFocus event for when the input element gains focus
  const onFocusProp = props.onFocus;
  const onFocus = useCallback(() => {
    onFocusProp?.();
  }, [onFocusProp]);

  // determine set type of input depending on passed type and state
  const [passwordShown, setPasswordShown] = useState(false);
  const showPasswordTool = props.type === 'password' && allowShowPassword;
  let inputType = props.type;
  if (props.type === 'password' && allowShowPassword && passwordShown) {
    inputType = 'text';
  }

  // get all the css classes to set to the container div
  const cssClasses = ['ehs-input-field', 'ehs-text-input-field'];
  if (props.leftIcon) {
    cssClasses.push('has-left-icon');
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS2590: Expression produces a union type that is too complex to represent
  if (showPasswordTool || props.rightIcon) {
    cssClasses.push('has-right-icon');
  }
  if (props.valueEmpty || (props.valueEmpty === undefined && valueEmpty)) {
    cssClasses.push('value-empty');
  }
  if (props.error) {
    cssClasses.push('invalid');
  } else cssClasses.push('valid');
  if (multiline) {
    cssClasses.push('multiline');
  }
  if (props.disabled) {
    cssClasses.push('disabled');
  }
  if (props.labelOnTop && !condensed) {
    cssClasses.push('labelontop');
  }

  const placeholder = props.placeholder;
  const inputHintId = useMemo(() => {
    return placeholder ? _.uniqueId('ehs-text-input-hint-') : undefined;
  }, [placeholder]);

  // properties passed to the main input/textarea element
  const inputElementProps = {
    className: 'input',
    autoComplete: props.autoComplete,
    inputMode: props.inputMode,
    autoFocus: props.autoFocus,
    onChange: onChange
      ? (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
          onChange?.(e.target.value)
      : undefined,
    defaultValue: props.defaultValue,
    disabled: props.disabled,
    value: props.value,
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyDown: props.onKeyDown,
    onPaste: props.onPaste,
    'aria-invalid': !!props.error,
    'aria-required': props.required,
    'aria-readonly': readOnly,
    'aria-label': props['aria-label'],
    'aria-describedby':
      props['aria-describedby'] ?? (multiline ? undefined : inputHintId),
    readOnly: readOnly
  };

  const label = props.required ? `${props.label} *` : props.label;

  // return the created element
  return (
    <div
      className={`ehs-input-field-wrapper ${
        className + (condensed ? ' condensed' : '')
      }`.trim()}
    >
      <div className={cssClasses.join(' ')}>
        <label>
          <span className="label">{label}</span>
          {multiline && (
            <textarea
              ref={ref as Ref<HTMLTextAreaElement>}
              rows={multilineRows}
              {...inputElementProps}
            />
          )}
          {!multiline && (
            <input
              ref={ref as Ref<HTMLInputElement>}
              type={inputType}
              {...inputElementProps}
            />
          )}
          {props.error && (
            <Icon
              className="ehs-input-error-icon"
              aria-hidden={true}
              aria-label=""
              iconName="shui_error"
            />
          )}
        </label>
        {props.leftIcon && (
          <Icon
            aria-label={undefined}
            iconName={props.leftIcon}
            className="left"
          />
        )}
        {showPasswordTool && !props.rightIcon && (
          <button
            className="password-tool ehs-tool transparent right"
            aria-label={intl('view.button.show_password')}
            type="button"
            role="switch"
            aria-checked={passwordShown}
            onClick={(): void => setPasswordShown(!passwordShown)}
          >
            <Icon
              aria-label=""
              iconName={
                passwordShown
                  ? 'materialicon-visibility'
                  : 'materialicon-visibility_off'
              }
            />
          </button>
        )}
        {props.rightIcon &&
          !showPasswordTool &&
          (isValidElement(props.rightIcon) ? (
            props.rightIcon
          ) : (
            <Icon
              aria-label={undefined}
              iconName={props.rightIcon as ValidIconName}
              className="right"
            />
          ))}
      </div>
      {placeholder && (
        <p
          id={inputHintId}
          className={`ehs-input-hint${props.disabled ? ' disabled' : ''}`}
        >
          {placeholder}
        </p>
      )}
      {props.error && <ValidationErrorMessage error={props.error} />}
    </div>
  );
});
